import {h, Fragment} from "preact"
import { SerpElement } from '@nosto/preact'
import { useEffect, useMemo, useState } from 'preact/hooks'
import { formatCurrency, currency, addTrailingZeros, handleize, getHandle, normalize } from '../../helpers'

import BadgesComponent from "./BadgesComponent"
import TagsComponent from "./TagsComponent"
import VariantsWishlist from "./VariantsWishlist"

export default ({ product }) => { 
	if (true) {
		// DYNAMIC CARD IMPLEMENTATION (Teamwork ticket ID 16857829)
		return (
			<div class="nosto-item ns-product product-block collection-wrapper__card ns-text-undecorated ns-color-inherit ns-p-2 ns-my-2 ns-mx-0">
				<nosto-dynamic-card handle={getHandle(product.url)} section="nosto-product-card" />
			</div>
		);
	} else {
		// PRODUCT CARD REDESIGN (Wrike ticket ID 1726895087 / Teamwork ticket ID 16796371)
		const { 
			productId,
			url: productUrl, 
			name: productTitle, 
			listPrice: compareAtPrice, 
			price, 
			availability: available, 
			skus: productVariants, 
			tags1: productTags,
			imageUrl: primaryImageUrl, 
			alternateImageUrls,
			customFields
		} = product;

		const currentColor = productVariants[0]?.customFields?.find(k => k.key == "color")?.value?.replaceAll("[", "").replaceAll("]", "").replaceAll('"', "").replaceAll('&quot;', '')
		const ageGroup = customFields?.find(k => k.key == "custom-age_group")?.value || '';
		const productLicense = customFields?.find(k => k.key == "custom-product_license")?.value;
		const productCollaboration = customFields?.find(k => k.key == "custom-collaboration")?.value;

		// Accessible product title
		const accessibleProductTitle = useMemo(() => {
			if (!theme.nostoMarkup.productCardAccessiblTitleText) {
				return productTitle;
			}
		
			if (currentColor) {
				return theme.nostoMarkup.productCardAccessiblTitleText
					.replace('%%%title%%%', productTitle)
					.replace('%%%color%%%', currentColor);
			}
		
			return productTitle;
		}, [productTitle, currentColor]);

		// Custom product title
		const customProductTitle = useMemo(() => {
			const showAgeGroup = ageGroup && !ageGroup.toLowerCase().includes('adult');

			const parts = [];

			if (productCollaboration) {
				parts.push(<>{productCollaboration}<span class="product-card__title-divider"></span></>);
			} else if (productLicense) {
				parts.push(<>{productLicense}<span class="product-card__title-divider"></span></>);
			}

			parts.push(productTitle);

			if (showAgeGroup) {
				parts.push(<><span class="product-card__title-divider"></span>{ageGroup}</>); 
			}

			return <>{parts}</>;
		}, [productTitle, productCollaboration, productLicense, ageGroup]);

		// Available colors count
		function getGroupTag(tags) {
			if (!tags) return null;
			return tags.find(t => t.toLowerCase().includes('group')) || null;
		}

		async function fetchVariantCount(group) {
			if (!group) return 0;

			const isDev = location.host === "shop-melissa-dev.myshopify.com";
			const isProd = location.host === "www.shopmelissa.com";

			let key;
			
			if (isDev) {
				key = "UCekm2BcKPE2kWlgt5ucGu1imTtocmcEPmN6pqa4vycYKVtJ6T8xueb5Beck9T9D"
			} else if (isProd) {
				key = "m8l3TqfeK27N3AEdw9tfifNdsW3X8JnjFPNu3hyi1U2zfQlVzeNurEbMqwevbJwd"
			}
		
			const query = `
				query {
					products(filter:{ tags1: "${group}", availability: "InStock" }, limit: 100) {
						products {
							productId
						}
					}
				}
			`;
		
			const res = await fetch('https://api.nosto.com/v1/graphql', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/graphql',
					'Authorization': 'Basic ' + btoa(':' + key)
				},
				mode: 'cors',
				body: query
			});
		
			const data = await res.json();
			return data?.data?.products?.products?.length || 0; 
		}

		const [colorsCount, setColorsCount] = useState(null);
		const [productColorsCountText, setProductColorsCountText] = useState('');

		useEffect(() => {
			const groupTag = getGroupTag(productTags);
			if (!groupTag) return;

			fetchVariantCount(groupTag).then(count => {
				if (count > 0) {
					setColorsCount(count);

					const text = window.theme?.nostoMarkup?.productCardAvailableColorsText ? window.theme?.nostoMarkup?.productCardAvailableColorsText.replace('%%%count%%%', count) : '';
					setProductColorsCountText(text);
				}
			});
		}, [productTags]);

		// Promo badges
		const [promoBadge, setPromoBadge] = useState(null);

		function getPromoBadge(productTags, promoConfig) {
			if (!promoConfig || !productTags) return null;
		
			for (const tag of productTags) {
				const key = normalize(tag);
				if (promoConfig[key]) {
					return promoConfig[key];
				}
			}
		
			return null;
		}

		useEffect(() => {
			const promoConfig = window?.theme?.productPromotionalBadges;
			const promoBadge = getPromoBadge(productTags, promoConfig);
			if (promoBadge) {
				setPromoBadge(promoBadge);
			}
			}, [productTags]);
		
		// Sizes availability panel
		const [availabilityMarkup, setAvailabilityMarkup] = useState('');

		useEffect(() => {
			const productHandle = getHandle(productUrl);
			const fragmentUrlTemplate = window.theme?.nostoMarkup?.productCardAvailabilityPanelMarkupUrl;

			if (!productHandle || !fragmentUrlTemplate) return;
			
			(async () => {
				try {
					const url = `${window.Shopify.routes.root}${fragmentUrlTemplate.replace('{{handle}}', productHandle)}`;
					const response = await fetch(url);
					if (!response.ok) throw new Error(`Network error: ${response.statusText}`);

					const markup = await response.text();
					setAvailabilityMarkup(markup);
				} catch (error) {
					console.error(`Failed to fetch availability fragment for ${productHandle}:`, error);
				}
			})();
		}, [productUrl]);

		// Media component
		const mediaElement = renderProductMedia(primaryImageUrl, alternateImageUrls, productUrl);

		function renderProductMedia(primaryImageUrl, alternateImageUrls = [], productUrl) {
			const formatImage = (url) => url?.toString()?.replace('.jpg', '_400x.jpg').replace('.webp', '_400x.webp');

			const images = [primaryImageUrl, ...alternateImageUrls].map(formatImage);

			if (images.length > 2) {
				return (
					<product-card-slider
						className="swiper slider product-card__slider"
						prev-button="product-card-slider-button[type='prev']"
						next-button="product-card-slider-button[type='next']"
						pagination="product-card-slider-pagination"
						is-nested-in-slider={false}
					>
						<div className="swiper-wrapper slider__wrapper product-card__slider-wrapper">
							{images.slice(0, 3).map((image, index) => (
								<div key={index} className="swiper-slide slider__slide product-card__slider-slide">
									<a href={productUrl} className="product-card__image-wrapper">
										<picture>
											<source
												srcSet={`${image} 1x, ${image.replace('_400x', '_800x')} 2x`}
												media="(max-width: 375px)"
											/>
											
											<img
												src={image}
												srcSet={`${image} 1x, ${image.replace('_400x', '_800x')} 2x`}
												alt=""
												className="product-card__image"
												loading="lazy"
											/>
										</picture>
									</a>
								</div>
							))}
						</div>

						<product-card-slider-button type="prev" class="swiper-button-prev slider__navigation-button slider__navigation-button--prev product-card__slider-navigation-button product-card__slider-navigation-button--prev"></product-card-slider-button>
						<product-card-slider-button type="next" class="swiper-button-next slider__navigation-button slider__navigation-button--next product-card__slider-navigation-button product-card__slider-navigation-button--next"></product-card-slider-button>

						<product-card-slider-pagination class="swiper-pagination product-card__slider-pagination"></product-card-slider-pagination>
					</product-card-slider>
				);
			} else {
				return (
					<a href={productUrl} className="product-card__image-wrapper">
						<picture>
							<source
							srcSet={`${images[0]} 1x, ${images[0].replace('_400x', '_800x')} 2x`}
							media="(max-width: 375px)"
							/>

							<img
							src={images[0]}
							srcSet={`${images[0]} 1x, ${images[0].replace('_400x', '_800x')} 2x`}
							alt=""
							className="product-card__image"
							loading="lazy"
							/>
						</picture>
					</a>
				);
			}
		}

		// Price component
		const priceElement = useMemo(() => {
			const savings = compareAtPrice > 0 ? Math.round(((compareAtPrice - Number(price)) * 100) / Number(compareAtPrice)) : 0;
			const savingsText = Number(savings) > 0 && window.theme?.nostoMarkup?.productCardSavingsTag ? window.theme?.nostoMarkup?.productCardSavingsTag.replace('%%%savings%%%', savings) : '';

			return (
				<>
				{price !== compareAtPrice ? (
					<>
						<span class="product-card__price-value product-card__price-value--compare-price ns-listPrice nosto-listprice">{currency}{addTrailingZeros(compareAtPrice)}</span>
						<span class="product-card__price-value product-card__price-value--sale nosto-price">{currency}{addTrailingZeros(price)}</span>

						{savingsText && (<div class="product-card__savings-wrapper" dangerouslySetInnerHTML={{__html: savingsText}}></div>)}
					</>
				) : (
					<>
						<span class="product-card__price-value nosto-price">{currency}{addTrailingZeros(price)}</span>
					</>
				)}
				</>
			)
		}, [])

		// Badges component
		const badgeElement = useMemo(() => {
			if (!productTags) {
				return null;
			}
		
			const tagsLowercased = productTags.map(tag => tag.toLowerCase());

			const isSale = compareAtPrice > price;
			const isCollab = Boolean(productCollaboration);
			const isNew = tagsLowercased.includes('new');
			const isOnlineExclusive = tagsLowercased.includes('online exclusive') || tagsLowercased.includes('online only');
			const isFavorite = tagsLowercased.includes('favorite');
			const isAvailable = product.available && !tagsLowercased.includes('sellout');
			const isComingSoon = !product.available && tagsLowercased.includes('coming soon');
		
			if (isSale) 
				return <div dangerouslySetInnerHTML={{ __html: window.theme.productBadges.sale }} />;

			if (isOnlineExclusive) 
				return <div dangerouslySetInnerHTML={{ __html: window.theme.productBadges.online_exclusive }} />;

			if (isNew) 
				return <div dangerouslySetInnerHTML={{ __html: window.theme.productBadges.new }} />;

			if (isCollab) 
				return <div dangerouslySetInnerHTML={{ __html: window.theme.productBadges.collab }} />;

			if (isFavorite) 
				return <div dangerouslySetInnerHTML={{ __html: window.theme.productBadges.favorite }} />;

			if (!isAvailable) 
				return <div dangerouslySetInnerHTML={{ __html: window.theme.productBadges.sold_out }} />;

			if (isComingSoon) 
				return <div dangerouslySetInnerHTML={{ __html: window.theme.productBadges.coming_soon }} />;
		
			return null;
		}, [product, productTags, compareAtPrice, price, productCollaboration]);

		return (
			<SerpElement
				as="div"
				hit={product}
				nosto-handle={getHandle(productUrl)}
				class="nosto-item ns-product product-block collection-wrapper__card ns-text-undecorated ns-color-inherit ns-p-2 ns-my-2 ns-mx-0"
			>
				<div class="product-card" data-product-card>
					<div class="product-card__media">
						{mediaElement}

						{badgeElement && (
							<div class="product-card__badges">{badgeElement}</div>
						)}

						{availabilityMarkup && (
							<div dangerouslySetInnerHTML={{ __html: availabilityMarkup }}/>
						)}
					</div>

					<div class="product-card__info">
						<a href={productUrl} class="product-card__title" data-product-card-link>
							<span class="visually-hidden">{accessibleProductTitle}</span>
							<span aria-hidden="true">{customProductTitle}</span>
						</a>

						{<div class="product-card__rating nosto-reviews">
							<div data-oke-star-rating="" data-oke-reviews-product-id={`shopify-${productId}`} data-oke-rendered=""></div>
						</div>}

						<div className="product-card__price">
							{priceElement}
						</div>

						{promoBadge && (
							<div class='product-card__promo' dangerouslySetInnerHTML={{ __html: promoBadge }} />
						)}

						<div class="product-card__footer" data-product-card-footer>
							<div class="product-card__colors-count">
								{productColorsCountText && (
									<>
										<span class="product-card__colors-count-icon">
											<svg role="presentation" focusable="false" width="20" height="20" fill="none" viewBox="0 0 22 22" class="icon icon--colors"><ellipse cx="10.696" cy="7.37" fill="#EE8847" rx="6.806" ry="6.804"></ellipse><ellipse cx="6.806" cy="15.147" fill="#DA9A9E" rx="6.806" ry="6.804" style="mix-blend-mode:multiply"></ellipse><ellipse cx="14.583" cy="15.147" fill="#6FBFDD" rx="6.806" ry="6.804" style="mix-blend-mode:multiply"></ellipse></svg>
										</span>

										<span class="product-card__colors-count-value">{productColorsCountText}</span>
									</>
								)}
							</div>
						</div>
					</div>
				</div>
			</SerpElement>
		);
		
		// Old product cards design
		if (false) {
			const { productId, imageUrl, name, brand, price, listPrice, url, alternateImageUrls, customFields, tags1, availability, skus: { id }, skus } = product;

			const [promoValue, setPromoValue] = useState(null)
			const [promoTag, setPromoTag] = useState(null)
		
			const license = customFields?.find(k => k.key == "custom-product_license")?.value
			const collab = customFields?.find(k => k.key == "custom-collaboration")?.value
			const currentColor = skus[0]?.customFields?.find(k => k.key == "color")?.value?.replaceAll("[", "").replaceAll("]", "").replaceAll('"', "").replaceAll('&quot;', '')
		
			let altImg = alternateImageUrls && alternateImageUrls[0] ? alternateImageUrls[0]?.toString()?.replace('.jpg', '_400x.jpg').replace('.webp', '_400x.webp') : imageUrl?.toString()?.replace('.jpg', '_400x.jpg').replace('.webp', '_400x.webp')
		
			// PROMO TAGS LOGIC REWORK (Wrike ticket ID 1801692071)
			useEffect(() => {
				const promotionalBadges = window?.theme?.productPromotionalBadges;
				if (!promotionalBadges || !tags1) return;
		
				for (const tag of tags1) {
					const normalized = normalize(tag);
			
					if (promotionalBadges[normalized]) {
						setPromoValue(tag);
						setPromoTag(promotionalBadges[normalized]);
						break;
					}
				}
			}, [tags1]);
		
			// const hasPromo = tags1?.find(v => v.includes("promo-badge:"));
		
			// if (hasPromo) {
			// 	setPromoValue(handleize(tags1?.find(v => v.includes("promo-badge:")).split("promo-badge:")[1]))
			// }
		
			// useEffect(() => {
			// 	if (promoValue != null) {
			// 		setPromoTag(window?.theme?.productPromotionalBadges?.[promoValue] ?? null)
			// 	}
			// }, [hasPromo, promoValue, product]) 
		
			// useEffect(() => {
			//     removeEmptyElements()
			// }, [product])
		
			const priceEl = useMemo(() => {
				const savings = listPrice > 0 ? Math.round(((listPrice - Number(price)) * 100) / Number(listPrice)) : 0;
				const savingsText = Number(savings) > 0 && window.theme?.nostoMarkup?.productCardSavingsTag ? window.theme?.nostoMarkup?.productCardSavingsTag.replace('%%%savings%%%', savings) : '';
		
				return (
					<>
					{price !== listPrice ? (
						<div class="product-card__price">
							<span class="product-card__price-value product-card__price-value--compare-price ns-listPrice nosto-listprice">{currency}{addTrailingZeros(listPrice)}</span>
							<span class="product-card__price-value product-card__price-value--sale nosto-price">{currency}{addTrailingZeros(price)}</span>
		
							{savingsText && (<div class="product-card__savings-wrapper" dangerouslySetInnerHTML={{__html: savingsText}}></div>)}
						</div>
					) : (
						<div class="product-card__price">
							<span class="product-card__price-value nosto-price">{currency}{addTrailingZeros(price)}</span>
						</div>
					)}
					</>
				)
			}, [])
		
			// SHOPLIFT CRO TEST AVAILABLE SIZES PANEL (WRIKE TICKET ID 1737640795)
			const showSizesAvailabilityPanel = true;
		
			const [availabilityMarkup, setAvailabilityMarkup] = useState('');
		
			useEffect(() => {
				const productHandle = getHandle(product.url);
		
				if (!productHandle) return;
		
				async function fetchAvailability() {
					const fragmentUrlTemplate = window.theme?.nostoMarkup?.productCardAvailabilityPanelMarkupUrl;
		
					if (!fragmentUrlTemplate) return;
		
					try {
						const response = await fetch(`${window.Shopify.routes.root}${fragmentUrlTemplate.replace('{{handle}}', productHandle)}`);
						if (!response.ok) throw new Error('Network error');
		
						const markup = await response.text();
						setAvailabilityMarkup(markup);
					} catch (error) {
						console.error('Failed to fetch availability fragment:', error);
					}
				}
		
				fetchAvailability();
				}, []);
		
			return (
				<SerpElement
					as={showSizesAvailabilityPanel ? 'div' : 'a'}
					hit={product}
					aria-label={`View ${name} in ${currentColor}`}
					nosto-handle={getHandle(product.url)}
					class={`nosto-item ns-product product-block collection-wrapper__card ns-border-box ${showSizesAvailabilityPanel ? '' : 'ns-clickable'} ns-text-undecorated ns-color-inherit ns-p-2 ns-my-2 ns-mx-0`}
				>
					<div 
						class="product-card ns-product-card" 
						data-product-card
					>
						<div class="product-card__media">
							<a href={url} class="product-card__image-wrapper nosto-product-url" aria-hidden="true" tabindex="-1">
								<picture>
									<img src={altImg} srcset={altImg} alt={`Product element, title ${name} in ${currentColor}`} class="product-card__image product-card__image--secondary" loading="lazy" role="presentation"
										aria-label={`Product element price ${currency}${addTrailingZeros(price)} select to view`} width="400" height="520"/>
								</picture>
								<picture>
									<img src={imageUrl?.toString()?.replace('.jpg', '_400x.jpg').replace('.webp', '_400x.webp')}
											srcset={imageUrl?.toString()?.replace('.jpg', '_400x.jpg').replace('.webp', '_400x.webp')}
											class="product-card__image" loading="lazy" role="presentation" alt={`Product element, title ${name} in ${currentColor}`} aria-label={`Product element price ${currency}${addTrailingZeros(price)} select to view`} width="400" height="520"/>
								</picture>
							</a>
		
							<div class="product-card__tags product-card__tags--top">
								<BadgesComponent
									listPrice={listPrice}
									price={price}
									tags1={tags1}
									name={name}
									collab={collab}
								/>
							</div>
		
							<TagsComponent
								customFields={customFields}
								tags1={tags1}
								availability={availability} 
							/>
		
							{showSizesAvailabilityPanel && <div dangerouslySetInnerHTML={{ __html: availabilityMarkup }}></div>}
						</div>
		
						<div class="product-card__info">
							<h3 class="product-card__vendor">
								<span class="product-card__vendor-value">{brand}</span>
								{collab ? (
									<span class="product-card__vendor-value"> + {collab}</span>
								) : license && (
									<span class="product-card__vendor-value"> + {license}</span>
								)}
							</h3>
		
							{showSizesAvailabilityPanel 
							? (
								<a href={url} class="product-card__title ns-prod-name" data-product-card-link wk-skip>
									<span class="visually-hidden">{`${name} in ${currentColor}`}</span>
									<span aria-hidden="true">{name}</span>
								</a>
							)
							: (
								<div class="product-card__title ns-prod-name" data-product-card-link wk-skip>
									<span class="visually-hidden">{`${name} in ${currentColor}`}</span>
									<span aria-hidden="true">{name}</span>
								</div>
							)
							
							}
		
							{<div class="product-card__rating nosto-reviews">
								<div data-oke-star-rating="" data-oke-reviews-product-id={`shopify-${productId}`} data-oke-rendered=""></div>
							</div>}
		
							{/* {price !== listPrice ? (
								<div class="product-info__price">
									<span class="product-info__price-value product-info__price-value--sale ns-listPrice nosto-listprice">{currency}{addTrailingZeros(listPrice)}</span>
									<span class="product-info__price-value product-info__price-value--compare-price nosto-price">{currency}{addTrailingZeros(price)}</span>
								</div>
							) : (
								<div class="product-card__price">
									<span class="product-card__price-value nosto-price">{currency}{addTrailingZeros(price)}</span>
								</div>
							)} */}
		
							{priceEl}
		
							{promoTag && (
								<div class="ns-promo-tag" dangerouslySetInnerHTML={{ __html: promoTag }}></div>
							)}
		
							<VariantsWishlist
								tags1={tags1}
								url={url}
								id={productId}
								name={name}
								currentColor={currentColor}
							/>
						</div>
					</div>
				</SerpElement>
			)
		}

	}
}
